import { useData } from "../../runtime/provider";
import { usePlayerActions } from "@utils/player/core/usePlayer";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { useInbox } from "@hooks/inbox/useInbox";
import { useSongs } from "@hooks/trpc/useSongs";
import { theme } from "@theme";

import { useQueries } from "@tanstack/react-query";
import { Href, router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { SectionRenderer } from "./InboxDetailSheet/SectionRenderer";
import type {
  MusicRecommendationsPayload,
  ImportedMusicPayload,
  TrackRef,
} from "./InboxDetailSheet/types";
import {
  getInboxDetailSections,
  getImportedMusicTracks,
  getInboxItemSummary,
  getSupportedArtists,
  getTopTrackPerArtist,
} from "./inboxPresentation";
import { SupportedArtistSongs } from "./SupportedArtistSongs";
import {
  getRecommendationPlaybackCollection,
  getSupportedArtistPlaybackCollection,
  type SupportedArtistSong,
} from "./inboxPlayback";

const InboxDetailScreen = () => {
  const session = useData();
  const playerActions = usePlayerActions();

  const { inboxItemId } = useLocalSearchParams<{ inboxItemId: string }>();
  const { inbox, isLoadingInbox, markAsRead } = useInbox();
  const markedItemRef = useRef<string | null>(null);
  const item = inbox.find((candidate) => candidate.id === inboxItemId);
  const supportedArtists = useMemo(() => getSupportedArtists(item), [item]);
  const artistSongQueries = useQueries({
    queries: supportedArtists.map((artist) => ({
      enabled: Boolean(artist.id),
      queryFn: () =>
        session.api.catalog.songs.getTopSongsByArtistId.query({
          artistId: artist.id,
          numberOfRecommendations: 1,
        }),
      queryKey: ["inbox", "supported-artist-song", artist.id],
      staleTime: 5 * 60 * 1000,
    })),
  });
  const supportedArtistSongs = supportedArtists.flatMap(
    (artist, index): SupportedArtistSong[] => {
      const song = artistSongQueries[index]?.data?.[0];
      return song ? [{ artist, song }] : [];
    },
  );
  const isLoadingSupportedArtistSongs = artistSongQueries.some(
    (query) => query.isLoading,
  );

  const detailTracks = useMemo(
    () =>
      getInboxDetailSections(item).flatMap((section) => {
        if (section.item === "music_recommendations") {
          return getTopTrackPerArtist(
            (section.payload as MusicRecommendationsPayload).tracks,
          );
        }
        if (section.item === "imported_music") {
          return getImportedMusicTracks(
            section.payload as ImportedMusicPayload,
          );
        }
        return [];
      }),
    [item],
  );
  const detailTrackIds = useMemo(
    () => detailTracks.map((track) => track.songId),
    [detailTracks],
  );
  const {
    query: { data: detailSongs = [] },
  } = useSongs(detailTrackIds, {
    enabled: detailTrackIds.length > 0,
  });

  useEffect(() => {
    if (item && !item.isRead && markedItemRef.current !== item.id) {
      markedItemRef.current = item.id;
      markAsRead([item.id]);
    }
  }, [item, markAsRead]);

  const playTrack = useCallback(
    (track: TrackRef) => {
      const song = detailSongs.find(
        (candidate) => candidate.id === track.songId,
      );
      if (!song) return;

      const collection = getRecommendationPlaybackCollection(song);
      if (collection) playerActions.playFromCollection(collection);
    },
    [detailSongs],
  );

  const playSupportedArtistSong = useCallback(
    (recommendation: SupportedArtistSong) => {
      const collection = getSupportedArtistPlaybackCollection(recommendation);
      if (collection) playerActions.playFromCollection(collection);
    },
    [],
  );

  if (!item) {
    return (
      <LScreen enableAutomaticTopPadding>
        <LBox alignItems="center" flex={1} justifyContent="center" gap="s3">
          {isLoadingInbox ? (
            <ActivityIndicator color={theme.colors.white} size="large" />
          ) : (
            <>
              <LText variant="h1">Notification unavailable</LText>
              <LText color="textGrey" textAlign="center" variant="bodyL">
                This notification may have expired or been removed.
              </LText>
            </>
          )}
        </LBox>
      </LScreen>
    );
  }

  const sections = getInboxDetailSections(item);

  return (
    <LScreen enableAutomaticTopPadding paddingHorizontal="zero">
      <ScrollView
        contentContainerStyle={{
          gap: theme.spacing.m1,
          paddingBottom: theme.spacing.l2,
          paddingHorizontal: theme.spacing.s4,
        }}
        showsVerticalScrollIndicator={false}
      >
        <LBox alignItems="center" flexDirection="row" gap="s3" pb="s3">
          <LArtwork
            borderRadius="l"
            size={64}
            url={item.imageUrl || item.image}
          />
          <LBox flex={1} gap="s1">
            <LText numberOfLines={2} variant="Heading/H2/SemiBold">
              {item.title}
            </LText>
            <LText color="subGrey" variant="Body/B2/Regular">
              {getInboxItemSummary(item)}
            </LText>
          </LBox>
        </LBox>

        {supportedArtists.length > 0 ? (
          <SupportedArtistSongs
            isLoading={isLoadingSupportedArtistSongs}
            onPlay={playSupportedArtistSong}
            recommendations={supportedArtistSongs}
          />
        ) : null}

        {sections.length > 0 ? (
          sections.map((section, index) => (
            <LBox key={`${section.item}-${index}`}>
              <SectionRenderer
                onNavigate={(relativeUrl) => {
                  if (relativeUrl) {
                    router.push(relativeUrl as Href);
                  }
                }}
                onPlayTrack={detailSongs.length > 0 ? playTrack : undefined}
                section={section}
              />
            </LBox>
          ))
        ) : supportedArtists.length === 0 ? (
          <LBox gap="s4">
            <LText color="subGrey" variant="Body/B1/Regular">
              {item.subtitle}
            </LText>
            {item.link ? (
              <SectionRenderer
                onNavigate={(relativeUrl) => {
                  if (relativeUrl) router.push(relativeUrl as Href);
                }}
                section={{
                  item: "fallback_link",
                  title: "",
                  text: "",
                  ctaText: "Open",
                  ctaUrl: item.link,
                  payload: {},
                }}
              />
            ) : null}
          </LBox>
        ) : null}
      </ScrollView>
    </LScreen>
  );
};

export default InboxDetailScreen;
